/** Public zone the ambient Worker binds. Overridable at the edge via `env.ZONE`. */
export const ZONE = "over9000.cc";

export const SURFACES = [
  "app",
  "cli",
  "api",
  "desk",
  "edge",
  "land",
  "data",
  "vid",
  "studio",
] as const;

export type Surface = (typeof SURFACES)[number];

export const SURFACE_PATTERN = SURFACES.join("|");

export function isSurface(value: string): value is Surface {
  return (SURFACES as readonly string[]).includes(value.toLowerCase());
}

/** Slug label used in host and path doors. No dots. */
export const SLUG_PATTERN = "[a-z0-9-]+";

/**
 * First labels that are never app slugs.
 * `www` is the hub host. `shots` / `posters` / `brand` / `assets` / `src` are hub SPA paths
 * (screenshots, modal posters, logos, Vite build, Vite dev). `sitemaps` is the hub sitemap
 * directory (`/sitemaps/*.xml`); without it that path is a door.
 * Surface names stay metadata and must not be registered or routed as slugs.
 */
export const HUB_PATH_LABELS = ["www", "shots", "posters", "brand", "assets", "src", "sitemaps"] as const;

/**
 * First labels that must never be registry apps. `id.over9000.cc` is the IdP
 * (Worker `over9000-id`), `mail.over9000.cc` is the mailroom (Worker
 * `over9000-mailroom`), and `admin.over9000.cc` is the studio admin console
 * (Worker `over9000-admin`). They share the one-label shape of a host door,
 * so `id`, `mail`, and `admin` are load-bearing and are not registry apps.
 * See docs/auth/PLAN.md §3.3.
 *
 * `id-staging` is the staging IdP host (`id-staging.{zone}`), reserved the
 * same way as `id`. `staging` is reserved so bare `staging.{zone}` is not an
 * app door. App staging doors are `{slug}-staging.{zone}` — the slug itself
 * must not end in `-staging`. See docs/STAGING.md.
 */
export const AUTH_RESERVED_SLUGS = [
  "id",
  "id-staging",
  "staging",
  "mail",
  "auth",
  "login",
  "logout",
  "account",
  "accounts",
  "sso",
  "oauth",
  "oidc",
  "openid",
  "signin",
  "signup",
  "www",
  "api",
  "hub",
  "admin",
  "o9k",
] as const;

export function isReservedSlug(value: string): boolean {
  const label = value.toLowerCase();
  return (
    isSurface(label) ||
    (HUB_PATH_LABELS as readonly string[]).includes(label) ||
    (AUTH_RESERVED_SLUGS as readonly string[]).includes(label)
  );
}

export function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
